import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Building, Save, MapPin, Phone, Mail, FileText, Loader2, Lock,
  Truck, Copy, RefreshCw, CheckCircle, ShieldCheck, Clock, Trash2,
  X, Check, ShieldOff, AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status-Badge
const statusConfig = {
  pending:  { label: 'Wartend',     bg: 'bg-amber-50 dark:bg-amber-900/20',  text: 'text-amber-600 dark:text-amber-400',  border: 'border-amber-200 dark:border-amber-700',  Icon: Clock },
  trusted:  { label: 'Verifiziert', bg: 'bg-green-50 dark:bg-green-900/20',  text: 'text-green-600 dark:text-green-400',  border: 'border-green-200 dark:border-green-700',  Icon: CheckCircle },
  blocked:  { label: 'Blockiert',   bg: 'bg-red-50   dark:bg-red-900/20',    text: 'text-red-600   dark:text-red-400',    border: 'border-red-200   dark:border-red-700',    Icon: AlertTriangle },
};

const SenderRow = ({ sender, actionLoading, onApprove, onProcessOnce, onReject, onRemove }) => {
  const cfg = statusConfig[sender.status] || statusConfig.pending;
  const { Icon } = cfg;
  const busy = !!actionLoading;

  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-3 py-2.5 rounded-lg border ${cfg.border} ${cfg.bg} transition-all`}
      data-testid={`sender-row-${sender.status}`}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <Icon className={`h-4 w-4 flex-shrink-0 ${cfg.text}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{sender.email}</p>
          {sender.first_seen_at && (
            <p className="text-xs text-gray-400">
              Erstkontakt: {new Date(sender.first_seen_at).toLocaleDateString('de-DE')}
            </p>
          )}
        </div>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.bg} ${cfg.text} ${cfg.border} shrink-0`}>
          {cfg.label}
        </span>
      </div>

      {/* Aktions-Buttons für pending */}
      {sender.status === 'pending' && (
        <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap">
          {/* Einmalig verarbeiten */}
          <button
            onClick={onProcessOnce}
            disabled={busy}
            title="Einmalig verarbeiten (Absender bleibt ungeprüft)"
            data-testid="process-once-sender-btn"
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-amber-100 hover:bg-amber-200 dark:bg-amber-900/30 dark:hover:bg-amber-900/50 text-amber-700 dark:text-amber-400 disabled:opacity-40 transition-colors whitespace-nowrap"
          >
            {actionLoading === 'once'
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Check className="h-3.5 w-3.5" />}
            Einmal freigeben
          </button>
          {/* Dauerhaft freigeben */}
          <button
            onClick={onApprove}
            disabled={busy}
            title="Dauerhaft freigeben (Absender auf Whitelist)"
            data-testid="approve-sender-btn"
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-green-100 hover:bg-green-200 dark:bg-green-900/30 dark:hover:bg-green-900/50 text-green-700 dark:text-green-400 disabled:opacity-40 transition-colors whitespace-nowrap"
          >
            {actionLoading === 'approve'
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Check className="h-3.5 w-3.5" />}
            Dauerhaft freigeben
          </button>
          {/* Ablehnen */}
          <button
            onClick={onReject}
            disabled={busy}
            title="Nicht freigeben (Absender blockieren)"
            data-testid="reject-sender-btn"
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-400 disabled:opacity-40 transition-colors whitespace-nowrap"
          >
            {actionLoading === 'reject'
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <X className="h-3.5 w-3.5" />}
            Nicht freigeben
          </button>
        </div>
      )}

      {/* Entfernen für trusted/blocked */}
      {(sender.status === 'trusted' || sender.status === 'blocked') && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={onRemove}
            disabled={busy}
            title="Von Whitelist entfernen"
            data-testid="remove-sender-btn"
            className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-red-500 dark:hover:text-red-400 disabled:opacity-40 transition-colors"
          >
            {actionLoading === 'remove'
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Trash2 className="h-3.5 w-3.5" />}
          </button>
        </div>
      )}
    </div>
  );
};

export const CompanySettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [initialIndustry, setInitialIndustry] = useState(null);
  const [importEmail, setImportEmail] = useState('');
  const [generatingEmail, setGeneratingEmail] = useState(false);
  const [copied, setCopied] = useState(false);
  // Trusted Senders
  const [senders, setSenders] = useState([]);
  const [sendersLoading, setSendersLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [formData, setFormData] = useState({
    company_name: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_email: '',
    phone: '',
    mobile: '',
    tax_number: '',
    vat_id: '',
    address: '', // For autocomplete search display
    street: '',
    house_number: '',
    postal_code: '',
    city: '',
    industry: 'tankstelle'
  });

  useEffect(() => {
    fetchCompanyData();
    fetchSenders();
  }, []);

  const fetchCompanyData = async () => {
    try {
      const response = await axios.get(`${API}/admin/company`);
      const data = response.data;
      
      // Split contact_name if exists
      const nameParts = data.contact_name?.split(' ') || ['', ''];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      setFormData(prev => ({
        ...prev,
        ...data,
        contact_first_name: firstName,
        contact_last_name: lastName
      }));
      setImportEmail(data.import_email || '');
      // Store initial industry to check if it's tankstelle (locked)
      setInitialIndustry(data.industry);
    } catch (error) {
      toast.error('Fehler beim Laden der Firmendaten');
    } finally {
      setLoading(false);
    }
  };

  const fetchSenders = async () => {
    setSendersLoading(true);
    try {
      const r = await axios.get(`${API}/admin/trusted-senders`);
      setSenders(r.data);
    } catch {
      // Kein Fehler anzeigen wenn leer
    } finally {
      setSendersLoading(false);
    }
  };

  const handleApproveSender = async (senderId) => {
    setActionLoading(p => ({ ...p, [senderId]: 'approve' }));
    try {
      const r = await axios.post(`${API}/admin/trusted-senders/${senderId}/approve`);
      toast.success(
        r.data.import_result && !r.data.import_result.error
          ? 'Absender freigegeben & Lieferung importiert'
          : 'Absender dauerhaft freigegeben'
      );
      setSenders(p => p.map(s => s.id === senderId ? { ...s, status: 'trusted' } : s));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Freigeben');
    } finally {
      setActionLoading(p => ({ ...p, [senderId]: null }));
    }
  };

  const handleProcessOnceSender = async (senderId) => {
    setActionLoading(p => ({ ...p, [senderId]: 'once' }));
    try {
      const r = await axios.post(`${API}/admin/trusted-senders/${senderId}/process-once`);
      toast.success(
        r.data.import_result && !r.data.import_result.error
          ? 'Einmalig verarbeitet — Absender nicht whitelisted'
          : 'Einmalige Freigabe erteilt'
      );
      setSenders(p => p.filter(s => s.id !== senderId));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler bei einmaliger Freigabe');
    } finally {
      setActionLoading(p => ({ ...p, [senderId]: null }));
    }
  };

  const handleRejectSender = async (senderId) => {
    setActionLoading(p => ({ ...p, [senderId]: 'reject' }));
    try {
      await axios.post(`${API}/admin/trusted-senders/${senderId}/reject`);
      toast.success('Absender abgelehnt und blockiert');
      setSenders(p => p.map(s => s.id === senderId ? { ...s, status: 'blocked' } : s));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Ablehnen');
    } finally {
      setActionLoading(p => ({ ...p, [senderId]: null }));
    }
  };

  const handleRemoveSender = async (senderId) => {
    setActionLoading(p => ({ ...p, [senderId]: 'remove' }));
    try {
      await axios.delete(`${API}/admin/trusted-senders/${senderId}`);
      toast.success('Absender entfernt');
      setSenders(p => p.filter(s => s.id !== senderId));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Entfernen');
    } finally {
      setActionLoading(p => ({ ...p, [senderId]: null }));
    }
  };

  const handleGenerateImportEmail = async (regenerate = false) => {
    setGeneratingEmail(true);
    try {
      const endpoint = regenerate ? 'regenerate' : 'generate';
      const r = await axios.post(`${API}/admin/import-email/${endpoint}`);
      setImportEmail(r.data.import_email);
      toast.success(regenerate ? 'Neue Import-E-Mail generiert' : 'Import-E-Mail generiert');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Generieren');
    } finally {
      setGeneratingEmail(false);
    }
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(importEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = async () => {
    // Validation
    if (!formData.mobile) {
      toast.error('Mobilnummer ist ein Pflichtfeld');
      return;
    }
    if (!formData.contact_email) {
      toast.error('E-Mail ist ein Pflichtfeld');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/admin/company`, {
        ...formData,
        contact_name: `${formData.contact_first_name} ${formData.contact_last_name}`.trim()
      });
      toast.success('Firmendaten gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
            Firmendaten
          </h1>
          <p className="text-muted-foreground mt-2">
            Verwalten Sie Ihre Unternehmensinformationen
          </p>
        </div>

        {/* Unternehmensdaten */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              Unternehmensdaten
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">Firmenname</Label>
                <Input
                  id="company_name"
                  value={formData.company_name || ''}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="Ihre Firma GmbH"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact_first_name">Ansprechpartner Vorname</Label>
                  <Input
                    id="contact_first_name"
                    value={formData.contact_first_name || ''}
                    onChange={(e) => setFormData({ ...formData, contact_first_name: e.target.value })}
                    placeholder="Max"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_last_name">Ansprechpartner Nachname</Label>
                  <Input
                    id="contact_last_name"
                    value={formData.contact_last_name || ''}
                    onChange={(e) => setFormData({ ...formData, contact_last_name: e.target.value })}
                    placeholder="Mustermann"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="industry" className="flex items-center gap-2">
                Branche
                {initialIndustry === 'tankstelle' && (
                  <Lock className="h-4 w-4 text-gray-500" />
                )}
              </Label>
              <select
                id="industry"
                value={formData.industry || 'tankstelle'}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                disabled={initialIndustry === 'tankstelle'}
                className={`w-full h-10 rounded-md border border-input px-3 ${
                  initialIndustry === 'tankstelle' 
                    ? 'opacity-60 cursor-not-allowed bg-gray-100' 
                    : 'bg-background'
                }`}
              >
                <option value="tankstelle">Tankstelle</option>
                <option value="gastronomie">Gastronomie</option>
                <option value="hotel">Hotel</option>
                <option value="kiosk">Kiosk</option>
                <option value="einzelhandel">Einzelhandel</option>
                <option value="sonstige">Sonstige</option>
              </select>
              {initialIndustry === 'tankstelle' && (
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <span>ℹ️</span>
                  <span>Ihre Branche kann nur über den Support geändert werden</span>
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Adresse */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Firmenadresse
            </CardTitle>
            <CardDescription>
              Nutzen Sie die Adresssuche oder geben Sie die Adresse manuell ein
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Manual Address Fields */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="postal_code">PLZ *</Label>
                <Input
                  id="postal_code"
                  value={formData.postal_code || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                    setFormData({ ...formData, postal_code: value });
                  }}
                  placeholder="73760"
                  maxLength={5}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="city">Ort *</Label>
                <Input
                  id="city"
                  value={formData.city || ''}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Fellbach"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="street">Straße *</Label>
                <Input
                  id="street"
                  value={formData.street || ''}
                  onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                  placeholder="Höhenstraße"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="house_number">Hausnummer *</Label>
                <Input
                  id="house_number"
                  value={formData.house_number || ''}
                  onChange={(e) => setFormData({ ...formData, house_number: e.target.value })}
                  placeholder="12"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Kontaktdaten */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              Kontaktdaten
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Telefonnummer</Label>
                <Input
                  id="phone"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+49 123 456789"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mobile" className="flex items-center gap-1">
                  Mobilnummer <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="mobile"
                  value={formData.mobile || ''}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  placeholder="+49 170 1234567"
                  required
                  className={!formData.mobile ? 'border-red-300' : ''}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact_email" className="flex items-center gap-1">
                <Mail className="h-4 w-4" />
                E-Mail <span className="text-red-500">*</span>
              </Label>
              <Input
                id="contact_email"
                type="email"
                value={formData.contact_email || ''}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                placeholder="kontakt@firma.de"
                required
                className={!formData.contact_email ? 'border-red-300' : ''}
              />
            </div>
          </CardContent>
        </Card>

        {/* Automatischer Lieferschein-Import */}
        <Card data-testid="import-email-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Rechnungs-Import-E-Mail
            </CardTitle>
            <CardDescription>
              Allgemeine Fallback-Adresse – die spezifische Import-Adresse pro Lieferant findest du unter <strong>Lieferungen → Lieferanten</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {importEmail ? (
              /* Read-Only – einmal generiert, nicht mehr änderbar */
              <div className="space-y-3">
                <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Diese Adresse ist <strong>fest vergeben</strong> und kann aus Sicherheitsgründen nicht geändert werden.
                    Teile sie deinen Lieferanten einmalig mit.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Deine Import-E-Mail-Adresse</label>
                  <div className="flex gap-2">
                    <input
                      readOnly
                      value={importEmail}
                      className="flex-1 h-10 rounded-md border bg-muted px-3 text-sm font-mono text-muted-foreground cursor-default select-all"
                      data-testid="import-email-readonly"
                    />
                    <button
                      onClick={handleCopyEmail}
                      className="flex items-center gap-1.5 px-3 h-10 rounded-md border bg-background text-sm hover:bg-muted transition-colors"
                      title="Kopieren"
                      data-testid="copy-import-email-btn"
                    >
                      {copied ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* Noch nicht generiert */
              <div className="space-y-3">
                <div className="flex items-start gap-3 px-4 py-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <Mail className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">Noch keine Import-Adresse angelegt</p>
                    <p className="text-sm text-blue-700 dark:text-blue-400 leading-relaxed">
                      Generiere einmalig eine feste E-Mail-Adresse im Format{' '}
                      <span className="font-mono bg-blue-100 dark:bg-blue-800/40 px-1.5 py-0.5 rounded text-xs">import-XXXXXXXX@allflowx.de</span>.
                      Diese bleibt dauerhaft fest – du kannst sie sicher an Lieferanten weitergeben.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => handleGenerateImportEmail(false)}
                  disabled={generatingEmail}
                  variant="outline"
                  className="gap-2"
                  data-testid="generate-import-email-btn"
                >
                  {generatingEmail ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Wird generiert…</>
                  ) : (
                    <><RefreshCw className="h-4 w-4" /> Import-Adresse jetzt generieren</>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Verifizierte Absender */}
        <Card data-testid="trusted-senders-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" />
              Verifizierte Absender
            </CardTitle>
            <CardDescription>
              Nur freigegebene E-Mail-Adressen dürfen automatisch Lieferscheine importieren
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sendersLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : senders.length === 0 ? (
              <div className="text-center py-6 space-y-2">
                <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto">
                  <ShieldOff className="h-5 w-5 text-gray-400" />
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Noch keine Absender registriert</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Sobald ein Lieferant eine E-Mail an Ihre Import-Adresse sendet, erscheint er hier.
                </p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="sender-list">
                {senders.map(sender => (
                  <SenderRow
                    key={sender.id}
                    sender={sender}
                    actionLoading={actionLoading[sender.id]}
                    onApprove={() => handleApproveSender(sender.id)}
                    onProcessOnce={() => handleProcessOnceSender(sender.id)}
                    onReject={() => handleRejectSender(sender.id)}
                    onRemove={() => handleRemoveSender(sender.id)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button onClick={handleSave} disabled={saving} className="w-full" size="lg">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Speichern...' : 'Alle Änderungen speichern'}
        </Button>
      </div>
    </DashboardLayout>
  );
};
